import React, {useEffect, useState} from 'react';
import {StyleSheet, RefreshControl} from 'react-native';
import {Box, Heading, VStack, HStack, ScrollView} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';

import DashboardTemplate from '../../../../../components/templates/dashboard';
import {BalanceIcon, PaymentsIcon} from '../../../../../components/icons';
import {BalanceItem, DashboardItem} from '../../../../../components/dashboard';
import {StackParamList} from '../../../../../navigators';
import {useContextState as userContext} from '../../../../../context/user';
import {useContextState as userSubClientContext} from '../../../../../context/sub-client';
import {SpinnerModal} from '../../../../../components/modals';
import useToast from '../../../../../hooks';
import {message as t} from '../../../../../utils/messages';
import * as Keychain from '../../../../../utils/keychain';

type ScreenProps = NativeStackScreenProps<StackParamList, 'Dashboard'>;

export const DashboardScreen = ({navigation}: ScreenProps) => {
  const {
    signOff,
    notifications,
    fetchNotificationList,
    profile,
    goToMessages,
    setGoToMessages,
  } = userContext();

  const {fetchBalanceSheet, balanceSheet} = userSubClientContext();

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const {showServerError} = useToast();

  const getNotifications = async () => {
    if (await fetchNotificationList()) {
      return;
    }
    throw new Error('Internal error');
  };

  const getBalanceSheet = async () => {
    if (await fetchBalanceSheet()) {
      return;
    }
    throw new Error('Internal error');
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      await getNotifications();
      await getBalanceSheet();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  useEffect(() => {
    if (goToMessages) {
      setGoToMessages(false);
      handleTapNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToMessages]);

  useEffect(() => {
    if (balanceSheet) {
      return;
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSheet]);

  const refreshData = async () => {
    await getData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const handleShowBalanceListForPayments = () => {
    navigation.navigate('UnpaidedBillListScreen');
  };

  const handleTapNotifications = () => {
    navigation.navigate('Notifications');
  };

  const handleShowProfile = () => {
    navigation.navigate('Profile');
  };

  const handleLogOut = () => {
    signOff();
    Keychain.resetGenericPassword();
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return '';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleTapProdile = () => {
    handleShowProfile();
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      notifications={notifications ? notifications : []}
      onTapNotifications={() => handleTapNotifications()}
      onTapLogout={() => handleLogOut()}
      onTapProfile={() => handleTapProdile()}
      canCallCenter={true}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={balanceSheet && balanceSheet ? t('updating') : undefined}
        />
        <Box borderRadius="md" marginTop={22}>
          <VStack space={3} justifyContent="center" marginX={4}>
            <DashboardItem
              label="dashboard-balances-item"
              icon={<BalanceIcon width={40} style={styles.balanceIcon} />}
              shadow={true}>
              <BalanceItem
                localBalance={balanceSheet?.local}
                foreignBalance={balanceSheet?.foreign}
                showAuxLabel={false}
              />
            </DashboardItem>
            <DashboardItem
              highlight={true}
              title={
                <HStack marginTop={1}>
                  <Heading
                    color={'#FF8C00'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={2}
                    fontStyle="italic">
                    {t('dashboard-item-enchanced-pay').toUpperCase()}{' '}
                  </Heading>
                  <Heading
                    color={'#0088FF'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={2}
                    fontStyle="italic">
                    {t('dashboard-item-enchanced-pay-invoice').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              summary="dashboard-easy-and-quickly"
              icon={
                <Box marginTop={2}>
                  <PaymentsIcon width={40} />
                </Box>
              }
              onPress={() => handleShowBalanceListForPayments()}
            />
          </VStack>
        </Box>
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});
